/**
 * Copyright 2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { Button, FlexContainer, Message, Text, theme } from "@nordcloud/gnui";
import {
  EventsFilterByFields,
  EventStatus,
  useGetEventsViewQuery,
} from "~/generated/graphql";
import { useQueryState } from "~/hooks";
import { isNotEmpty, inflect } from "~/tools";
import { eventsExcludeFilterMap } from "~/tools/filterStateMaps";
import { createFilterByInput } from "~/utils/createFilterByInput";
import { EventType } from "../types";
import { QueryGetEvents } from "./constants";
import { prepareEventsFilterBy } from "./eventFilterBuilders";

export function EventListApprovalMessage() {
  const { queryState, updateQueryState } = useQueryState<QueryGetEvents>();
  const isFutureEvents = queryState.eventsType === EventType.future;
  const phrase = isNotEmpty(queryState.searchPhrase?.trim() || "")
    ? queryState.searchPhrase
    : undefined;
  const shouldShowApprovalMessage =
    !isFutureEvents && !hasActiveEventFilters(queryState);

  const pendingApprovalFilterBy = [
    ...prepareEventsFilterBy({
      ...queryState,
      eventStatuses: [],
      excludeEventStatuses: false,
    }),
    ...createFilterByInput<EventsFilterByFields>(
      EventsFilterByFields.Status,
      [EventStatus.WaitingForApproval],
      false
    ),
  ];

  const { data, loading } = useGetEventsViewQuery({
    variables: {
      page: 1,
      limit: 1,
      filter: {
        filterBy: pendingApprovalFilterBy,
        phrase,
      },
    },
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "network-only",
    skip: !shouldShowApprovalMessage,
  });

  const approvalCount = data?.eventsView.pageInfo?.count ?? 0;

  if (!shouldShowApprovalMessage || loading || approvalCount === 0) {
    return null;
  }

  const handleShowEvents = () => {
    updateQueryState({
      ...queryState,
      eventStatuses: [EventStatus.WaitingForApproval],
      excludeEventStatuses: false,
      page: "1",
    });
  };

  return (
    <Message
      color={theme.color.support.purple}
      borderColor={theme.color.support.purple}
      image="warning"
      css={{
        marginBottom: theme.spacing.spacing02,
        div: { marginBottom: 0, alignSelf: "center" },
      }}
    >
      <FlexContainer justifyContent="space-between" css={{ width: "100%" }}>
        <Text weight="medium" marginBottom={0}>
          {approvalCount} {inflect("event")(approvalCount)} waiting for approval
        </Text>
        <Button status="accent" onClick={handleShowEvents}>
          Show {inflect("Event")(approvalCount)}
        </Button>
      </FlexContainer>
    </Message>
  );
}

function hasActiveEventFilters(queryState: QueryGetEvents): boolean {
  return (
    (Object.values(eventsExcludeFilterMap) as Array<keyof QueryGetEvents>).some(
      (k) => ((queryState[k] as string[] | undefined)?.length ?? 0) > 0
    ) ||
    (Object.keys(eventsExcludeFilterMap) as Array<keyof QueryGetEvents>).some(
      (k) => (queryState[k] as boolean | undefined) ?? false
    )
  );
}
